import { createSelector, Reducer } from '@reduxjs/toolkit';
import { Location } from 'react-router-dom';

import { Events as facebookEvents, track as facebookTrack } from '@/components/FacebookPixel/FacebookPixel';
import { modalTypes, PromotionPlanTypeId } from '@/enums';
import api from '@/redux/api/buyNowRequest';
import { getAuthToken, getBidderId } from '@/redux/modules/account/user/user.selectors';
import { getAnalyticsCatalogRegistrationStatus } from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import { getDeployment } from '@/redux/modules/config';
import { getCurrencyConversion } from '@/redux/modules/currency';
import { AppDispatch, AppGetState, GlobalState } from '@/redux/store';
import { trackBidPlacedAnalytics, TrackBidPlacedBidType, trackItemSavedAnalytics } from '@/utils/amplitude/analytics';
import { getUserPrebidStatus } from '@/utils/amplitude/analytics/bidding/biddingAnalytics.utils';
import { ButtonLocation } from '@/utils/amplitude/analytics/item/trackItemSaved.analytics';
import { trackShareButtonClickedAnalytics } from '@/utils/amplitude/analytics/sharing/trackShareButtonClicked.analytics';
import { getFullUrl } from '@/utils/amplitude/analytics/utils/getFullUrl';
import { getAmplitudePageNameFromPath } from '@/utils/getPageNameFromPath';
import { parseQueryParams } from '@/utils/queryParams';
import { separateURL } from '@/utils/urls';

import { recordMarketingReportsClick, recordMarketingReportsImpression } from '../api/analytics';
import { getCatalogSellerId } from './catalog/catalogs/catalog.selectors';
import {
    getItemSummaryBidderHasBid,
    getItemSummaryBidderHasHighBid,
    getItemSummaryCatalogId,
    getItemSummaryCurrency,
    getItemSummaryIsReserveMet,
    getItemSummaryPromotionPlanTypeId,
    getItemSummarySellerId,
} from './item/summary/itemSummary.selectors';
import { getItemFacets } from './itemFacets';

const getFeaturedLotsEventType = (planTypeId?: PromotionPlanTypeId) =>
    planTypeId === PromotionPlanTypeId.FeaturedLotsBulk ? 'Featured Lots Bulk' : 'Featured Lots';

const KNOWN_SEARCH_ENGINES = [
    '.msn.',
    '.search.com',
    'about.com',
    'adsensecustomsearchads.com',
    'alice.',
    'alltheweb.com',
    'altavista.com',
    'applebot',
    'ask.com',
    'avg.com',
    'babylon.com',
    'baidu.',
    'biglobe.',
    'bing.',
    'centrum.cz',
    'cnn.com/search',
    'daum.net',
    'delta-search.com',
    'discretesearch.com',
    'duckduckgo.com',
    'easydialsearch.com',
    'ekolay.net',
    'eniro.se',
    'haosou.com',
    'kvasir.no',
    'lycos.',
    'mamma.com',
    'mynet.com',
    'myprivatesearch.com',
    'mysearchdial.com',
    'najdi.',
    'naver.com',
    'onet.pl',
    'onetcenter.',
    'ozu.es',
    'pchome.com',
    'pinterest.com',
    'query.searchdconvertnow.com',
    'rakuten.',
    'rambler.ru',
    'search.aol',
    'search.avira.net',
    'search.bt.com',
    'search.centurylink.com',
    'search.colonymt.com',
    'search.earthlink.net',
    'search.excite.com',
    'search.frontier.com',
    'search.media.telstra.com',
    'search.mediacomcable.com',
    'search.monstercrawler.com',
    'search.myway.com',
    'search.pch.com',
    'search.smt.docomo',
    'search.socialdownloadr.com',
    'search.twcc.com',
    'search.us.com',
    'search.xfinity.com',
    'search.yahoo.com',
    'searchall.com',
    'searchassist.verizon.com',
    'searchguide.windstream.net',
    'searchlock.com',
    'searchprivacy.co',
    'searcht.sosodesktop.com',
    'searchthis.com',
    'securesearch.co',
    'sesam.no',
    'seznam.cz',
    'so.com',
    'sogou.com',
    'startsiden.no',
    'szukacz.pl',
    'terra.com',
    'thesmartsearch.net',
    'utorrent.inspsearch.com',
    'virgilio.it',
    'voila.fr',
    'w.goo.ne.jp',
    'w.google.',
    'w.yahoo.com',
    'wp.pl',
    'www.delta-search.com',
    'yam.com',
    'yandex.',
    'yoursearch.me',
    'zdsearch.com',
];
const KNOWN_SOCIAL_NETWORKS = [
    'pinterest.',
    'twitter.',
    '/t.co',
    'facebook.',
    'yuku.',
    'netvibes.',
    'blogspot.',
    'houzz.',
    'polyvore.',
    'youtube.',
    'vk.com',
    'paper.li',
    'reddit.',
    'buzzfeed.',
    'plus.google.',
    'plus.url.google',
    'ravelry',
    'wikia.',
    'wordpress.',
    'blog.goo.ne',
    'linkedin.',
    'groups.google.',
    '.ning.',
    'stackexchange.',
    'stumbleupon.',
    'tumblr.',
    'typepad.',
    'answers.yahoo.',
];
const IGNORED_REFERRERS = ['liveauctioneers.', 'optimizely.', 'atlassian.', 'localhost', 'rainforestqa.'];

/* ANALYTIC TYPES */

// THIS OBJECT SHOULD BE COMMON ACROSS THE APPS.  IF YOU UPDATE IT HERE, UPDATE IT EVERYWHERE
export const AnalyticsTypes = {
    Page: {
        AboutUs: 'about us',
        AccountSettings: 'account settings',
        ACHTermsAndConditions: 'ach terms and conditions',
        AddCreditCard: 'add credit card',
        AuctionCalendar: 'catalog search',
        AuctionRegistration: 'auction registration',
        AuctionResults: 'auction results',
        BiddingConsole: 'bidding console',
        CardOnFile: 'card on file',
        Catalog: 'catalog',
        CatalogTerms: 'terms and conditions',
        Category: 'category',
        CertCapture: 'cert capture',
        Checkout: 'checkout page',
        CheckoutConfirmation: 'checkout confirmation',
        Consignments: 'consignments',
        CookiePolicy: 'cookie policy',
        CuratedCollection: 'curated collection',
        Discover: 'discover',
        Features: 'features',
        FollowedSellers: 'followed auctioneers',
        GetHelp: 'get help',
        Home: 'home',
        HowAuctionsWork: 'how it works',
        IntellectualProperty: 'intellectual property',
        Item: 'item',
        ItemPhotoViewer: 'item photo viewer',
        Message: 'message',
        Messages: 'messages',
        Mobile: 'mobile',
        MyAuctions: 'my auctions',
        MyBidsPast: 'my bids - past tab',
        MyBidsUpcoming: 'my bids - upcoming tab',
        MyProfile: 'my profile',
        NearMe: 'near me',
        Notifications: 'notifications',
        NotificationsPage: 'notifications page',
        PasswordReset: 'password reset',
        PriceGuide: 'price guide',
        PriceResult: 'price result',
        Privacy: 'privacy',
        PRServices: 'pr services',
        PushNotifications: 'push notifications',
        RecentItems: 'recent items',
        RecommendedItems: 'recommended items',
        Review: 'review',
        SavedItems: 'saved items',
        SavedSearchManage: 'saved searches - manage tab',
        SavedSearchRecent: 'followed searches',
        Search: 'search dropdown',
        SearchItems: 'search',
        Seller: 'auctioneer',
        SellerDirectory: 'directory',
        SellerReviews: 'reviews',
        SellerTestimonials: 'testimonials',
        SimilarItems: 'similar items',
        SoldItem: 'sold item',
        Storefront: 'storefront',
        Terminology: 'terminology',
        TermsAndConditions: 'terms and conditions',
        TermsAndConditionsPayments: 'terms and conditions payments',
        TrendingAuctions: 'trending auctions',
        WonItems: 'won items',
    },
    Track: {
        CompleteProfile: 'Completed Profile',
        FollowSeller: 'Followed Auctioneer',
        MessagedSeller: 'Messaged Seller',
        PlaceAbsenteeBid: 'Placed Absentee Bid',
        PlacedWinningBid: 'Placed Winning Bid',
        PlaceLiveBid: 'Placed Live Bid',
        PlaceSearch: 'Placed Search',
        RegisterForAuction: 'Requested Auction Registration',
        RegisterUser: 'Registered Account',
        RetractAbsenteeBid: 'Retracted Absentee Bid',
        SaveEasyShippingItem: 'Saved Easy Shipping Item',
        SaveItem: 'Saved Item',

        SaveSearch: 'Followed Search',
        SendSellerMessage: 'Messaged Seller',
        SendSupportMessage: 'Messaged Support',
        SetPassword: 'Set Password',
        ShareItem: 'Social Network Share',
        StartedAccountCreation: 'Started Account Creation',
        StartedAuctionRegistration: 'Started Auction Registration',
        StartedLogIn: 'Started Log-In',
        UnfollowSeller: 'Unfollowed Auctioneer',
        UnsaveItem: 'Unsaved Item',
        UnsaveSearch: 'Unfollowed Search',
        UserAuthenticated: 'User Authenticated',
        ViewEasyShippingItem: 'View Easy Shipping Item',
    },
} as const;

export type AnalyticsTypesTrackKeys = keyof typeof AnalyticsTypes.Track;
export type AnalyticsTypesPageKeys = keyof typeof AnalyticsTypes.Page;
export type AnalyticsTypesValues =
    | (typeof AnalyticsTypes.Track)[AnalyticsTypesTrackKeys]
    | (typeof AnalyticsTypes.Page)[AnalyticsTypesPageKeys];

/**
 * Analytics event contexts
 */
export enum EventContext {
    'autoPopModal' = 'Auto-Pop Modal',
    'consign' = 'Consign',
    'exitIntentModal' = 'Exit Intent Modal',
    'followAuctioneer' = 'Follow Auctioneer',
    'followSearch' = 'Follow Search',
    'homepageBanner' = 'Homepage Banner',
    'join' = 'Join',
    'loginModal' = 'Log-In Modal',
    'placeBid' = 'Place Bid',
    'registerForAuction' = 'Register for Auction',
    'saveItem' = 'Save Item',
    'seeSimilarItems' = 'See Similar Items',
    'seeSoldPrice' = 'See Sold Price',
    'sendMessage' = 'Send Message',
    'signUpModal' = 'Sign-Up Modal',
    'social_register_already_exists' = 'social_register_already_exists',
}

export interface AnalyticsSlice {
    readonly bot: boolean;
    readonly referrer: string;
    readonly touchDate: number;
}

export const defaultAnalyticsSlice: AnalyticsSlice = {
    bot: false,
    referrer: '',
    touchDate: 0,
};

export const reducer: Reducer<AnalyticsSlice> = (state = defaultAnalyticsSlice) => state;

/* SELECTORS */

const stateSelector = (state: GlobalState) => state;
const analyticsSlice = createSelector(stateSelector, (state) => state.analytics);

const getReferrer = createSelector(analyticsSlice, (state) => state.referrer);

export const getIsBot = createSelector(analyticsSlice, (state) => state.bot);

const getTouchDate = createSelector(analyticsSlice, (state) => state.touchDate);

const searchList = (haystack, needle) => {
    let ret = -1;
    haystack.forEach((value) => {
        if (needle.toLowerCase().includes(value)) {
            ret = needle;
        }
    });
    return ret;
};

type GetAttributionDataQueryParams = {
    campaignSource: string;
    campaigncontent: string;
    campaignkeyword: string;
    campaignmedium: string;
    campaignname: string;
    campaignsource: string;
    gclid: string;
    gclsrc: string;
    utm_campaign: string;
    utm_content: string;
    utm_medium: string;
    utm_source: string;
    utm_term: string;
};

export type AttributionData = {
    bidderId: number;
    campaign: string;
    content: string;
    keyword: string;
    medium: string;
    source: string;
    touchDate: number;
};

export const getAttributionData = createSelector(
    [(state: GlobalState) => state, (_, location: Location) => location],
    (state, location) => {
        const { search } = location;
        const rawReferrer = getReferrer(state);
        const referrer = separateURL(rawReferrer);
        const bidderId = getBidderId(state);
        const touchDate = getTouchDate(state);

        let attributionData = {
            bidderId,
            campaign: '',
            content: '',
            keyword: '',
            medium: '',
            source: '',
            touchDate,
        };

        const engineName = searchList(KNOWN_SEARCH_ENGINES, rawReferrer);
        const networkName = searchList(KNOWN_SOCIAL_NETWORKS, rawReferrer);
        const referrerName = searchList(IGNORED_REFERRERS, rawReferrer);
        const queryParams = parseQueryParams<GetAttributionDataQueryParams>(search ?? '');

        if (queryParams.gclsrc) {
            attributionData = {
                ...attributionData,
                campaign: 'DoubleClick',
                content: 'DoubleClick',
                keyword: queryParams.gclsrc || '',
                medium: 'cpc',
                source: 'google',
            };
        } else if (queryParams.campaignsource || queryParams.campaignSource) {
            attributionData = {
                ...attributionData,
                campaign: queryParams.campaignname || '',
                content: queryParams.campaigncontent || '',
                keyword: queryParams.campaignkeyword || '',
                medium: queryParams.campaignmedium || '',
                source: queryParams.campaignsource || queryParams.campaignSource || '',
            };
        } else if (queryParams.utm_source) {
            attributionData = {
                ...attributionData,
                campaign: queryParams.utm_campaign || '',
                content: queryParams.utm_content || '',
                keyword: queryParams.utm_term || '',
                medium: queryParams.utm_medium || '',
                source: queryParams.utm_source || '',
            };
        } else if (engineName !== -1) {
            attributionData = {
                ...attributionData,
                content: referrer.path,
                medium: 'organic',
                source: referrer.host,
            };
        } else if (networkName !== -1) {
            attributionData = {
                ...attributionData,
                content: referrer.path,
                medium: 'social',
                source: referrer.host,
            };
        } else if (referrer.host !== '' && referrerName === -1) {
            attributionData = {
                ...attributionData,
                content: referrer.path,
                medium: 'referral',
                source: referrer.host,
            };
        } else {
            attributionData = {
                ...attributionData,
                campaign: 'direct',
                source: 'direct',
            };
        }
        return attributionData;
    }
);

// region Item analytics

export const getSaveItemAnalytics = (
    state: GlobalState,
    itemId: number,
    location: Location,
    buttonLocation: ButtonLocation
) => {
    const deployment = getDeployment(state);
    const { categories } = getItemFacets(state, itemId);

    facebookTrack(facebookEvents.SavedItem);
    trackItemSavedAnalytics({
        buttonLocation,
        itemId,
        lotCategoryLvl1: categories?.[0]?.l1CategoryName || 'Unknown',
        lotCategoryLvl2: categories?.[0]?.l2CategoryName || 'Unknown',
        pageName: getAmplitudePageNameFromPath(location.pathname),
        url: getFullUrl(deployment, location.pathname),
    });
};
export const getFollowSellerAnalytics = (state: GlobalState, sellerId: number) => {
    facebookTrack(facebookEvents.FollowedAuctioneer, { houseID: sellerId });
};

export const getRegisterAccountAnalytics = () => {
    facebookTrack(facebookEvents.RegisteredAccount);
};

export const getRequestedAuctionRegistrationAnalytics = (state: GlobalState, catalogId: number, itemId: number = 0) => {
    const houseId = getCatalogSellerId(state, catalogId);

    facebookTrack(facebookEvents.RegisterForAuction, {
        catalogID: catalogId,
        houseID: houseId,
        itemId,
    });
};
export const getPlacedBidAnalytics = (
    state: GlobalState,
    itemId: number,
    bidAmount: number,
    event: string,
    location: Location
) => {
    const bidderHasBid = getItemSummaryBidderHasBid(state, itemId);
    const bidderHasHighBid = getItemSummaryBidderHasHighBid(state, itemId);
    const catalogId = getItemSummaryCatalogId(state, itemId);
    const currency = getItemSummaryCurrency(state, itemId);
    const isReserveMet = getItemSummaryIsReserveMet(state, itemId);
    const sellerId = getItemSummarySellerId(state, itemId);

    if (event === AnalyticsTypes.Track.PlaceAbsenteeBid) {
        facebookTrack(facebookEvents.PlacedAbsenteeBid, {
            catalogID: catalogId,
            currency,
            houseID: sellerId,
            itemId,
        });
    }

    const deployment = getDeployment(state);
    const { categories } = getItemFacets(state, itemId);

    trackBidPlacedAnalytics({
        auctionRegistrationStatus: getAnalyticsCatalogRegistrationStatus(state, catalogId),
        bidAmount,
        bidAmountUsd: (() => {
            const conversion = getCurrencyConversion(state, currency);
            return conversion && typeof conversion.conversionToUsd === 'number'
                ? bidAmount * conversion.conversionToUsd
                : 0;
        })(),
        bidType:
            event === AnalyticsTypes.Track.PlaceAbsenteeBid
                ? TrackBidPlacedBidType.Absentee
                : TrackBidPlacedBidType.Live,
        catalogCurrency: currency,
        catalogId,
        itemId,
        lotCategoryLvl1: categories?.[0]?.l1CategoryLabel ?? 'Unknown',
        lotCategoryLvl2: categories?.[0]?.l2CategoryLabel ?? 'Unknown',
        pageName: getAmplitudePageNameFromPath(location.pathname),
        url: getFullUrl(deployment, location.pathname),
        userPrebidStatus: getUserPrebidStatus(bidderHasBid, bidderHasHighBid, isReserveMet),
    });
};

export const getPlacedLiveBidAnalytics = (state: GlobalState, itemId: number, bidAmount: number, location: Location) =>
    getPlacedBidAnalytics(state, itemId, bidAmount, AnalyticsTypes.Track.PlaceLiveBid, location);

/* ACTION CREATORS */

export const trackAdUnitClick =
    ({ adUnitId, consignment, page }: { adUnitId: number; consignment: boolean; page: string }) =>
    async (_dispatch: AppDispatch, getState: AppGetState) => {
        const state = getState();
        const bidderId = getBidderId(state);
        const deployment = getDeployment(state);

        // Call the marketing-reporting-service lambda to record the click
        if (page === 'calendar' && !consignment) {
            // Fire off Auction Calendar Banner Ad clicked
            recordMarketingReportsClick({
                bidderId,
                deployment,
                eventId: adUnitId,
                eventType: 'Auction Calendar Banner',
            });
        } else if (page === 'navigation') {
            // Fire off Category Navigation Ad clicked
            recordMarketingReportsClick({
                bidderId,
                deployment,
                eventId: adUnitId,
                eventType: 'Category Navigation Ad',
            });
        } else if (page.includes('Category Leaderboard Ad')) {
            // Fire off Category Banner Ad clicked
            recordMarketingReportsClick({
                bidderId,
                deployment,
                eventId: adUnitId,
                eventType: page,
            });
        } else if (page.includes('Search Page Banner')) {
            // Fire off Search Page Banner Ad clicked
            recordMarketingReportsClick({
                bidderId,
                deployment,
                eventId: adUnitId,
                eventType: page,
            });
        } else if (page.includes('Leaderboard Ad')) {
            // Fire off Category Navigation Ad clicked
            recordMarketingReportsClick({
                bidderId,
                deployment,
                eventId: adUnitId,
                eventType: page,
            });
        } else if (page.includes('home')) {
            // Fire off Featured Auctioneers Ad viewed
            recordMarketingReportsClick({
                bidderId,
                deployment,
                eventId: adUnitId,
                eventType: 'Featured Auctioneers Ad',
            });
        } else if (!consignment) {
            // Fire off Category Banner Ad clicked
            recordMarketingReportsClick({
                bidderId,
                deployment,
                eventId: adUnitId,
                eventType: 'Category Banner',
            });
        }
    };

export const trackAdUnitVisible =
    ({ adUnitId, page }: { adUnitId: number; page: string }) =>
    async (dispatch: AppDispatch, getState: AppGetState) => {
        const state = getState();
        const bidderId = getBidderId(state);
        const deployment = getDeployment(state);

        // Call the marketing-reporting-service lambda to record the impression
        if (page === 'calendar') {
            // Fire off Auction Calendar Banner Ad viewed
            recordMarketingReportsImpression({
                bidderId,
                deployment,
                eventId: adUnitId,
                eventType: 'Auction Calendar Banner',
            });
        } else if (page === 'navigation') {
            // Fire off Category Navigation Ad visible
            recordMarketingReportsImpression({
                bidderId,
                deployment,
                eventId: adUnitId,
                eventType: 'Category Navigation Ad',
            });
        } else if (page.includes('Category Leaderboard Ad')) {
            // Fire off Category Banner Ad visible
            recordMarketingReportsImpression({
                bidderId,
                deployment,
                eventId: adUnitId,
                eventType: page,
            });
        } else if (page.includes('Search Page Banner')) {
            // Fire off Search Page Banner Ad impression
            recordMarketingReportsImpression({
                bidderId,
                deployment,
                eventId: adUnitId,
                eventType: 'Search Page Banner',
            });
        } else if (page.includes('Leaderboard Ad')) {
            // Fire off Category Navigation Ad visible
            recordMarketingReportsImpression({
                bidderId,
                deployment,
                eventId: adUnitId,
                eventType: page,
            });
        } else if (page.includes('home')) {
            // Fire off Featured Auctioneers Ad viewed
            recordMarketingReportsImpression({
                bidderId,
                deployment,
                eventId: adUnitId,
                eventType: 'Featured Auctioneers Ad',
            });
        } else {
            // Fire off Category Banner Ad viewed
            recordMarketingReportsImpression({
                bidderId,
                deployment,
                eventId: adUnitId,
                eventType: 'Category Banner',
            });
        }
    };

export const recordSocialNetworkShare = (network: string, itemUrl: string) => async () => {
    trackShareButtonClickedAnalytics({
        itemUrl,
        network,
        pageName: getAmplitudePageNameFromPath(window.location.pathname),
        url: window.location.href,
    });
};

export const trackPromotedItemClick =
    ({ itemId }: { itemId: number }) =>
    async (_: AppDispatch, getState: AppGetState) => {
        const state = getState();
        const bidderId = getBidderId(state);
        const catalogId = getItemSummaryCatalogId(state, itemId);
        const deployment = getDeployment(state);
        const planTypeId = getItemSummaryPromotionPlanTypeId(state, itemId);

        recordMarketingReportsClick({
            bidderId,
            deployment,
            eventId: catalogId,
            eventType: getFeaturedLotsEventType(planTypeId),
            itemId,
        });
    };

export const trackBuyNowOfferSubmissionClick =
    (itemId: number, buyNowPrice: number, offerPrice: number) =>
    async (dispatch: AppDispatch, getState: AppGetState) => {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const sellerId = getItemSummarySellerId(state, itemId);

        let action;

        switch (state.makeOffer.modalType) {
            case modalTypes.MAKE_OFFER:
                action = 'Made An Offer';
                break;
            case modalTypes.BUY_NOW:
                action = 'Requested To Buy Now';
                break;
            default:
                break;
        }

        const details = {
            buyNowPrice,
            buyNowRequestType: action,
            houseId: sellerId,
            itemId,
            offerPrice,
        };

        api.postCaptureBuyNowRequest({ authToken, deployment, details });
    };

export const trackFeaturedCatalogView =
    ({ catalogId }: { catalogId: number }) =>
    async (AppDispatch, getState: AppGetState) => {
        const state = getState();
        const bidderId = getBidderId(state);
        const deployment = getDeployment(state);

        // Call the marketing-reporting-service lambda to record the impression
        recordMarketingReportsImpression({
            bidderId,
            deployment,
            eventId: catalogId,
            eventType: 'Featured Auction',
        });
    };

export const trackFeaturedCatalogClick =
    (catalogId: number) => async (_dispatch: AppDispatch, getState: AppGetState) => {
        const state = getState();
        const bidderId = getBidderId(state);
        const deployment = getDeployment(state);

        // Call the marketing-reporting-service lambda to record the click
        recordMarketingReportsClick({
            bidderId,
            deployment,
            eventId: catalogId,
            eventType: 'Featured Auction',
        });
    };

export const trackPromotedItemVisible =
    ({ itemId }: { itemId: number }) =>
    async (_dispatch: AppDispatch, getState: AppGetState) => {
        const state = getState();
        const bidderId = getBidderId(state);
        const catalogId = getItemSummaryCatalogId(state, itemId);
        const deployment = getDeployment(state);
        const planTypeId = getItemSummaryPromotionPlanTypeId(state, itemId);

        recordMarketingReportsImpression({
            bidderId,
            deployment,
            eventId: catalogId,
            eventType: getFeaturedLotsEventType(planTypeId),
            itemId,
        });
    };
